// Full Auto Coupons Targeting — daily snapshot of advertisers' readiness
// for the "fully automated coupons + targeting" capability. Modelled as a
// generic capability so future client programmes can reuse the same shape.

import { ITesterProfileSnapshot } from "./tester-profile";

export const FULL_AUTO_COUPONS_TARGETING_FLAG = "full-auto-coupons-targeting";

/**
 * Tri-state status for an eligibility check / overall snapshot:
 *  - "green":  everything required is present (all live coupons satisfy the check).
 *  - "amber":  some live coupons satisfy the check but not all — frontend will
 *              still surface the merchant, just with degraded coverage.
 *  - "red":    nothing satisfies the check — the requirement is completely missing.
 *  - "error":  a system/infrastructure failure (SQL timeout, API unreachable, etc.)
 *              prevented reliable computation. The snapshot data should NOT be
 *              treated as a truthful reflection of the item's state.
 */
export type FullAutoCouponsTargetingCheckStatus =
  | "green"
  | "amber"
  | "red"
  | "error";

/** Number of days a passing SDK test remains "fresh" (used by both backend and frontend). */
export const SDK_TEST_FRESH_DAYS = 15;

/**
 * The sample apps an SDK end-to-end test can be run against. This list is
 * intentionally data-driven so more targets (e.g. new partner apps) can be
 * added in one place without touching the form or the backend validation.
 */
export const SDK_TEST_SAMPLE_APPS = [
  { value: "kindred_sample_app", label: "Kindred Sample App" },
  { value: "samsung_apk", label: "Samsung APK" },
] as const;

export type SdkTestSampleApp = (typeof SDK_TEST_SAMPLE_APPS)[number]["value"];

export const SDK_TEST_SAMPLE_APP_VALUES: SdkTestSampleApp[] =
  SDK_TEST_SAMPLE_APPS.map((a) => a.value);

export const sdkTestSampleAppLabel = (value: string): string =>
  SDK_TEST_SAMPLE_APPS.find((a) => a.value === value)?.label ?? value;

/**
 * Timing buckets for how long a checkout test took to complete, in seconds.
 * "lt1".."lt6" mean "under N seconds"; "gt6" means "over 6 seconds".
 */
export const SDK_TEST_CHECKOUT_TIMINGS = [
  { value: "lt1", label: "<1s" },
  { value: "lt2", label: "<2s" },
  { value: "lt3", label: "<3s" },
  { value: "lt4", label: "<4s" },
  { value: "lt5", label: "<5s" },
  { value: "lt6", label: "<6s" },
  { value: "gt6", label: ">6s" },
  // Not a duration: the second checkout exists to prove the stand-down rule
  // fires, so there is nothing to time — only a pass/fail.
  { value: "stand_down", label: "Stand-down rule applies" },
] as const;

export type SdkTestCheckoutTiming =
  (typeof SDK_TEST_CHECKOUT_TIMINGS)[number]["value"];

export const SDK_TEST_CHECKOUT_TIMING_VALUES: SdkTestCheckoutTiming[] =
  SDK_TEST_CHECKOUT_TIMINGS.map((t) => t.value);

export const sdkTestCheckoutTimingLabel = (value: string): string =>
  SDK_TEST_CHECKOUT_TIMINGS.find((t) => t.value === value)?.label ?? value;

/** Languages selectable for the translation test / device language check. */
export const SDK_TEST_LANGUAGES = [
  { value: "spanish", label: "Spanish" },
  { value: "french", label: "French" },
  { value: "german", label: "German" },
  { value: "italian", label: "Italian" },
  { value: "portuguese", label: "Portuguese" },
  { value: "other", label: "Other" },
] as const;

export type SdkTestLanguage = (typeof SDK_TEST_LANGUAGES)[number]["value"];

export const SDK_TEST_LANGUAGE_VALUES: SdkTestLanguage[] =
  SDK_TEST_LANGUAGES.map((l) => l.value);

export const sdkTestLanguageLabel = (value: string): string =>
  SDK_TEST_LANGUAGES.find((l) => l.value === value)?.label ?? value;

/** One basket shape run through both checkout tests. */
export interface ISdkTestBasketTest {
  firstCheckoutResult: "pass" | "fail";
  firstCheckoutTiming: SdkTestCheckoutTiming;
  secondCheckoutResult: "pass" | "fail";
  secondCheckoutTiming: SdkTestCheckoutTiming;
}

/** Only required when the domain's `requiresTranslationTest` flag is on.
 *  Records which language the storefront was translated to and re-runs both
 *  checkout tests against that translated experience. */
export interface ISdkTestTranslationTest extends ISdkTestBasketTest {
  languageTested: SdkTestLanguage;
}

/** A single coupon code checked under the device's own language setting. */
export interface ISdkTestDeviceLanguageCode {
  code: string;
  passed: boolean;
}

/** Changes the device's own language and checks each code's description
 *  translates correctly — no checkout required. */
export interface ISdkTestDeviceLanguageCheck {
  deviceLanguage: SdkTestLanguage;
  codes: ISdkTestDeviceLanguageCode[];
}

export interface IFullAutoCouponsTargetingMetrics {
  hasDeal: boolean;
  dealStoreName: string | null;
  dealCashback: number | null;
  dealCashbackType: string | null;

  hasAutoTemplate: boolean; // from external autocoupons checkout (templates returned with skuRequest=null)
  autoTemplateCount: number;

  hasSkuTemplate: boolean; // from external autocoupons checkout when calling with a SKU request
  skuTemplateCount: number;

  hasAnyOffer: boolean;
  offerCount: number;

  hasAnySitewideCoupon: boolean;
  sitewideCouponCount: number;

  // SQL-derived (per brand+country)
  activeCouponCount: number;
  couponsWithMetadataCount: number;
  couponsWithTargetingCount: number;
  couponMetadataCoverageMismatchCount: number;

  metadataCoveragePct: number; // 0..1
  targetingCoveragePct: number; // 0..1

  /** SDK end-to-end test: green if most recent test is within SDK_TEST_FRESH_DAYS and passed. */
  sdkTestStatus: FullAutoCouponsTargetingCheckStatus;
  lastSdkTestResult: "pass" | "fail" | null;
  lastSdkTestAt: string | null;
  /**
   * Set by the agentic/session runner when a checkout session failed due to
   * template-logic issues (distinct from infrastructure errors in runErrors).
   * Drives the TemplateReview pipeline stage.
   */
  lastSessionRunFailed?: boolean;
  /** Full detail of the most recent SDK test as it stood when this snapshot was
   *  captured. Persisted against the snapshot so the detail page can pull the
   *  checkout results through without re-querying the test log. Null when no
   *  test had been recorded at snapshot time. */
  lastSdkTest: IFullAutoCouponsTargetingSDKTestSummary | null;

  /** Worst status across all eligibility checks. */
  overallStatus: FullAutoCouponsTargetingCheckStatus;
}

/** The subset of an SDK test record persisted against a snapshot's metrics. */
export interface IFullAutoCouponsTargetingSDKTestSummary {
  /** Overall result — "pass" only when every single- and multi-basket
   *  checkout passed. Translation results are recorded but don't gate. */
  result: "pass" | "fail";
  sampleApp: SdkTestSampleApp;
  firstCheckoutResult: "pass" | "fail";
  firstCheckoutTiming: SdkTestCheckoutTiming;
  secondCheckoutResult: "pass" | "fail";
  secondCheckoutTiming: SdkTestCheckoutTiming;
  multiBasketTest: ISdkTestBasketTest | null;
  reason: string;
  testedBy: string | null;
  createdAt: string;
}

export interface IFullAutoCouponsTargetingSnapshot {
  _id: string;
  snapshotDate: string; // YYYY-MM-DD (UTC)
  runId: string;
  qaItemId: string;
  brandId: string | null;
  brandName: string | null;
  domain: string;
  countryCode: string;
  metrics: IFullAutoCouponsTargetingMetrics;
  errors: string[];
  createdAt: string;
  updatedAt: string;
}

export interface IFullAutoCouponsTargetingRunResult {
  runId: string;
  snapshotDate: string;
  itemsConsidered: number;
  itemsProcessed: number;
  itemsFailed: number;
  startedAt: string;
  completedAt: string | null;
  durationMs: number;
}

export interface IFullAutoCouponsTargetingRunSummary extends IFullAutoCouponsTargetingRunResult {
  type: "bulk" | "single";
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  triggeredBy: string | null;
  totalBatches: number;
  completedBatches: number;
  failedBatches: number;
}

/** One 500-item processing unit within a run, owned by a single worker pod. */
export interface IFullAutoCouponsTargetingBatch {
  batchId: string;
  runId: string;
  batchIndex: number;
  status: "pending" | "claimed" | "completed" | "failed" | "cancelled";
  totalItems: number;
  processedCount: number;
  failedCount: number;
  workerId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
}

/** Full run detail including per-batch breakdown. */
export interface IFullAutoCouponsTargetingRunDetail {
  run: IFullAutoCouponsTargetingRunSummary;
  batches: IFullAutoCouponsTargetingBatch[];
}

/** Filters accepted by the runs list endpoint (all optional). */
export interface IFullAutoCouponsTargetingRunsFilter {
  /** "pending" | "running" | "completed" | "failed" | "cancelled" or "" for any. */
  status?: string;
  /** "bulk" | "single" or "" for any. */
  type?: string;
  /** Exact triggeredBy match or "" for any. */
  triggeredBy?: string;
  /** YYYY-MM-DD to filter one date, "all" for every date, or omitted to default
   *  to the latest run date. */
  snapshotDate?: string;
  page?: number;
  pageSize?: number;
}

/** Paginated + filtered runs list. */
export interface IFullAutoCouponsTargetingRunsResponse {
  /** The current page of runs matching the filter, newest first. */
  runs: IFullAutoCouponsTargetingRunSummary[];
  /** Count of ALL pending/running runs (any date), for the "Currently running"
   *  badge. The runs themselves are fetched lazily via the active-runs endpoint
   *  when that section is expanded, so a long backlog of stuck runs isn't
   *  loaded on every list request. */
  activeCount: number;
  /** True if a bulk run is currently pending/running — drives the toolbar
   *  "Run now" guard and cancel affordance without loading the active list. */
  activeBulkRunning: boolean;
  /** Total runs matching the filter (across all pages). */
  total: number;
  page: number;
  pageSize: number;
  /** The snapshotDate filter actually applied to `runs` (null = all dates). */
  snapshotDate: string | null;
  /** Distinct run snapshot dates, newest first — for the date picker. */
  availableDates: string[];
  /** Distinct non-empty triggeredBy values, ascending — for the picker. */
  triggeredByOptions: string[];
}

/** On-demand list of every pending/running run (any date), newest first.
 *  Loaded only when the "Currently running" section is expanded. */
export interface IFullAutoCouponsTargetingActiveRunsResponse {
  activeRuns: IFullAutoCouponsTargetingRunSummary[];
}

export interface IFullAutoCouponsTargetingFilter {
  date?: string; // YYYY-MM-DD; defaults to latest
  search?: string; // domain substring
  countryCode?: string;
  /** "green" | "amber" | "red" or empty for any. */
  overallStatus?: string;
  // Booleans expressed as "true" / "false" / "" via query string:
  hasDeal?: string;
  hasAutoTemplate?: string;
  hasSkuTemplate?: string;
  hasAnyOffer?: string;
  hasAnySitewideCoupon?: string;
  noActiveCoupons?: string;
  noMetadata?: string; // couponsWithMetadataCount == 0
  noTargeting?: string; // couponsWithTargetingCount == 0
  hasCoverageMismatch?: string;
  sdkTestPassed?: string; // "passed" | "failed" | "untested" — green / red / amber sdkTestStatus
}

export interface IFullAutoCouponsTargetingSnapshotsResponse {
  date: string;
  results: IFullAutoCouponsTargetingSnapshot[];
  total: number;
  page: number;
  pageSize: number;
  totals: {
    itemCount: number;
    greenCount: number;
    amberCount: number;
    redCount: number;
    errorCount: number;
    hasDealCount: number;
    hasAutoTemplateCount: number;
    hasSkuTemplateCount: number;
    hasSitewideCouponCount: number;
    withMetadataCount: number;
    withTargetingCount: number;
    coverageMismatchCount: number;
    sdkTestPassedCount: number;
  };
}

export interface IFullAutoCouponsTargetingHistoryResponse {
  domain: string;
  countryCode: string;
  results: IFullAutoCouponsTargetingSnapshot[];
}

// ── Detail / drill-down ──────────────────────────────────

export interface IFullAutoCouponsTargetingEligibilityCheck {
  key: string;
  label: string;
  status: FullAutoCouponsTargetingCheckStatus;
  detail: string;
}

export interface IFullAutoCouponsTargetingApiTemplate {
  priority: number;
  version: number;
  checkoutPageUrlRegex: string;
}

/**
 * SQL-sourced view of a deal/campaign for this (domain, country). Mirrors a
 * subset of `IKindredDealSummary` that the detail UI cares about.
 */
export interface IFullAutoCouponsTargetingSqlDeal {
  id: string;
  name: string;
  brandId: string;
  brandName: string;
  cashbackType: string;
  totalCommission: number | null;
  startDate: string;
  endDate: string;
  publishedStatus: number;
  isActive: boolean;
  suspended: boolean;
  source: string;
}

/** SQL-sourced view of an offer for this (domain, country). */
export interface IFullAutoCouponsTargetingSqlOffer {
  id: string;
  summary: string | null;
  validFrom: string;
  validTo: string;
  publishedStatus: number;
  deactivatedAt: string | null;
  isActive: boolean;
}

export interface IFullAutoCouponsTargetingApiData {
  // SQL-sourced (campaign table). The autocoupons/domain-search APIs are
  // partner-specific and shouldn't drive eligibility decisions here.
  deal: IFullAutoCouponsTargetingSqlDeal | null;
  // SQL-sourced (offer table) — active offers for (domain, country).
  offers: IFullAutoCouponsTargetingSqlOffer[];
  // From /partners/.../autocoupons/v1/checkout (skuRequest=null) — kept as-is
  // because templates are not modelled in SQL.
  autoTemplates: IFullAutoCouponsTargetingApiTemplate[];
  // From /partners/.../autocoupons/v1/checkout (encrypted SKU request).
  skuTemplates: IFullAutoCouponsTargetingApiTemplate[];
  errors: string[];
}

export interface IFullAutoCouponsTargetingSqlCoupon {
  couponId: string;
  code: string;
  couponHash: string | null;
  priority: number;
  validFrom: string | null;
  validTo: string | null;
  coverage: string | null;
  metadataCoverage: string | null;
  hasMetadata: boolean;
  coverageMatchesMetadata: boolean;
  targetingCount: number;
  hasTargeting: boolean;
  countriesEnabled: string[];
  /** Mirrors KindredPublishStatus from the QA list — Approved=2, etc. */
  publishedStatus: number;
  /** True if publishedStatus === Approved AND validFrom <= now <= validTo (matches QA-list "LIVE"). */
  isLive: boolean;
}

export interface IFullAutoCouponsTargetingDetailResponse {
  qaItemId: string;
  domain: string;
  countryCode: string;
  brandId: string | null;
  brandName: string | null;
  // Snapshot row this detail came from (or just-recomputed for live=true).
  snapshot: IFullAutoCouponsTargetingSnapshot;
  // Persisted (or just-fetched) capture of the autocoupons + domain-search APIs.
  api: IFullAutoCouponsTargetingApiData;
  // Persisted SQL coupon snapshot (LIVE only, taken at snapshot time).
  sqlCoupons: IFullAutoCouponsTargetingSqlCoupon[];
  // Eligibility breakdown, recomputable from the persisted data above.
  eligibilityChecks: IFullAutoCouponsTargetingEligibilityCheck[];
  overallStatus: FullAutoCouponsTargetingCheckStatus;
  /** Was this response generated by re-running live (?live=true) instead of
   *  served from the persisted snapshot? */
  live: boolean;
  /** When the underlying data was captured (snapshot.createdAt for persisted,
   *  now for live). */
  fetchedAt: string;
}

// ── Item management DTOs ─────────────────────────────────

/** A single SDK end-to-end test record for a tracked item. */
export interface IFullAutoCouponsTargetingSDKTest {
  id: string;
  itemId: string;
  /** Overall result — "pass" only when every single- and multi-basket
   *  checkout passed. Translation results are recorded but don't gate. */
  result: "pass" | "fail";
  /** Which sample app the test was run against. */
  sampleApp: SdkTestSampleApp;
  /** First checkout test. */
  firstCheckoutResult: "pass" | "fail";
  firstCheckoutTiming: SdkTestCheckoutTiming;
  /** Second checkout test. */
  secondCheckoutResult: "pass" | "fail";
  secondCheckoutTiming: SdkTestCheckoutTiming;
  /** 2x one item + 1x a second item. Required alongside the single basket;
   *  null only on tests recorded before multi-basket testing existed. */
  multiBasketTest: ISdkTestBasketTest | null;
  /** Optional free-text notes. */
  reason: string;
  testedBy: string | null;
  /** Tester's device/profile as it stood when this test was recorded. Null
   *  for tests recorded before profiles existed. */
  tester: ITesterProfileSnapshot | null;
  /** Only present when the domain requires a translation test. */
  translationTest: ISdkTestTranslationTest | null;
  /** Only present when a device language check was recorded. */
  deviceLanguageCheck: ISdkTestDeviceLanguageCheck | null;
  createdAt: string;
}

export interface IFullAutoCouponsTargetingSDKTestRequest {
  sampleApp: SdkTestSampleApp;
  firstCheckoutResult: "pass" | "fail";
  firstCheckoutTiming: SdkTestCheckoutTiming;
  secondCheckoutResult: "pass" | "fail";
  secondCheckoutTiming: SdkTestCheckoutTiming;
  multiBasketTest?: ISdkTestBasketTest;
  reason?: string;
  /** Which of the tester's saved device profiles this test was recorded on. */
  profileId?: string | null;
  translationTest?: ISdkTestTranslationTest;
  deviceLanguageCheck?: ISdkTestDeviceLanguageCheck;
}

export interface IFullAutoCouponsTargetingSDKTestsResponse {
  tests: IFullAutoCouponsTargetingSDKTest[];
  /** Echoes the tracked item's flag so the recording panel knows whether to
   *  show the translation test / device language check sections. */
  requiresTranslationTest: boolean;
  /** The languages this storefront is known to translate into. The tester
   *  picks the one they used from this list rather than the full catalogue. */
  translationLanguages: SdkTestLanguage[];
}

/** A single entry in the standalone full-auto-coupons tracking list.
 *  Completely independent of any QA list. */
export interface IFullAutoCouponsTargetingTrackedItem {
  itemId: string;
  domain: string;
  countryCode: string;
  addedBy: string | null;
  enabled: boolean;
  createdAt: string;
  /** Whether this domain requires the translation test / device language
   *  check sections when recording an APK test. Toggled per-domain by an
   *  admin/manager since only some storefronts are localised. */
  requiresTranslationTest: boolean;
  /** Which languages this storefront can be translated into. */
  translationLanguages: SdkTestLanguage[];
  /** Most recent SDK test for this item, if any. */
  lastSdkTest: IFullAutoCouponsTargetingSDKTest | null;
  /** Computed by the backend: green if lastSdkTest is pass and within SDK_TEST_FRESH_DAYS. */
  sdkTestStatus: FullAutoCouponsTargetingCheckStatus;
}

export interface IFullAutoCouponsTargetingItemSettingsRequest {
  requiresTranslationTest?: boolean;
  translationLanguages?: SdkTestLanguage[];
}

export interface IFullAutoCouponsTargetingItemsResponse {
  items: IFullAutoCouponsTargetingTrackedItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IFullAutoCouponsTargetingAddItemRequest {
  domain: string;
  countryCode: string;
}

export interface IFullAutoCouponsTargetingAddItemResponse {
  itemId: string;
  /** true = newly created, false = already existed (re-enabled if disabled) */
  created: boolean;
}

export interface IFullAutoCouponsTargetingResetResponse {
  removedCount: number;
}

// ── Live-items import ─────────────────────────────────────

/**
 * A single domain/country pair discovered by the live-import SQL query.
 * These are items that currently have:
 *   - an active checkout template (AutoCouponsTemplates.IsActive = 1)
 *   - at least one live deal (approved, not suspended, active now)
 *   - at least one live coupon code (approved, within validity dates)
 */
export interface IFullAutoCouponsTargetingLiveItem {
  domain: string;
  countryCode: string;
  brandId: string | null;
  brandName: string | null;
  liveCouponCount: number;
  /** Already present in the tracking list. */
  alreadyTracked: boolean;
}

export interface IFullAutoCouponsTargetingLivePreviewResponse {
  items: IFullAutoCouponsTargetingLiveItem[];
  total: number;
  newCount: number;
  alreadyTrackedCount: number;
}

export interface IFullAutoCouponsTargetingImportResponse {
  imported: number;
  skipped: number;
  runTriggered: boolean;
}

// ── Daily aggregate report ────────────────────────────────

/**
 * Per-day aggregate metrics used by the Reporting tab.
 *
 * - totalAutoTemplates / totalSkuTemplates  — total template instance counts
 *   (sum of autoTemplateCount / skuTemplateCount across all items for the day).
 * - totalActiveAutoTemplates / totalActiveSkuTemplates — count of items that
 *   have at least one template of that type.
 * - totalActiveCombined — items that have BOTH an auto template AND a SKU template.
 * - totalActiveCombinedBrokenTests — combined items whose SDK test is NOT green.
 * - totalActiveCombinedWithSitewideOrTargeting — combined items with a passing
 *   SDK test (green) that also have a sitewide coupon OR at least one coupon
 *   with targeting configured (broken-test items are excluded).
 */
export interface IFullAutoCouponsTargetingDailyReportEntry {
  date: string; // YYYY-MM-DD
  totalItems: number;
  totalAutoTemplates: number;
  totalSkuTemplates: number;
  totalActiveAutoTemplates: number;
  totalActiveSkuTemplates: number;
  totalActiveCombined: number;
  totalActiveCombinedBrokenTests: number;
  totalActiveCombinedWithSitewideOrTargeting: number;
}

export interface IFullAutoCouponsTargetingDailyReportResponse {
  entries: IFullAutoCouponsTargetingDailyReportEntry[];
}

// ── Template counts (standalone, per snapshot date) ───────────────────────────

/** Total SQL template counts, stored independently per snapshot date. */
export interface IFullAutoCouponsTargetingTemplateCounts {
  snapshotDate: string; // YYYY-MM-DD
  totalAutoTemplates: number | null;
  totalSkuTemplates: number | null;
  refreshedAt: string;
}

export interface IFullAutoCouponsTargetingTemplateCountsResponse {
  counts: IFullAutoCouponsTargetingTemplateCounts | null;
}
